import { DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import angularLocaleEn from '@angular/common/locales/en';
import angularLocaleNl from '@angular/common/locales/nl';
import { inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ApplicationConfig } from '@config/application.config';
import { getDatepickerConfig } from '@config/datepicker.config';
import MessageFormatConfig from '@config/message-format.config';
import NgxSpinnerConfig from '@config/ngx-spinner.config';
import NgxsStorageConfig from '@config/ngxs-storage.config';
import ToastrConfig from '@config/toastr.config';
import { LayoutModule } from '@core/layout/layout.module';
import { NavigationModule } from '@core/navigation/navigation.module';
import { LanguageVersion } from '@data/common/LanguageVersion';
import { LocaleId } from '@data/common/LocalId';
import { MeetingModule } from '@domain/meeting/meeting.module';
import { NgSelectConfig, NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule, Store } from '@ngxs/store';
import { AUTH_PROVIDER } from '@shared/common/injector-token';
import { AccessTokenInterceptor } from '@shared/interceptor/access-token.interceptor';
import { AppLanguageInterceptor } from '@shared/interceptor/app-language.interceptor';
import { AppTimezoneInterceptor } from '@shared/interceptor/app-timezone.interceptor';
import { AppVersionInterceptor } from '@shared/interceptor/app-version.interceptor';
import { ErrorInterceptor } from '@shared/interceptor/error.interceptor';
import { LoaderInterceptor } from '@shared/interceptor/loader.interceptor';
import { RefreshTokenInterceptor } from '@shared/interceptor/refresh-token.interceptor';
import { CognitoAuthProvider } from '@shared/provider/cognito-auth-provider.service';
import { SharedModule } from '@shared/shared.module';
import { ApplicationState } from '@shared/store/application/application.state';
import { AuthState } from '@shared/store/auth/auth.state';
import { FileUploadState } from '@shared/store/file/file-upload.state';
import { NavigationState } from '@shared/store/navigation/navigation.state';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { TranslateConfig } from 'app/config/translate.config';
import { ApplicationModel } from 'app/model/application.model';
import { defineLocale, nlLocale } from 'ngx-bootstrap/chronos';
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { enGbLocale } from 'ngx-bootstrap/locale';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ToastrModule } from 'ngx-toastr';
import { MESSAGE_FORMAT_CONFIG } from 'ngx-translate-messageformat-compiler';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ScreeningQuestionnaireState } from '@shared/store/screening-questionnaire/screening-questionnaire.state';
import { RegistrationState } from '@shared/store/registration/registration.state';
import { MoodState } from 'app/view/journal/state/mood.state';
import { PatientDailyNotesState } from 'app/view/journal/state/patient-daily-notes.state';

registerLocaleData(angularLocaleEn, LanguageVersion.EN);
registerLocaleData(angularLocaleNl, LanguageVersion.NL);

defineLocale('en-gb', enGbLocale);
defineLocale('nl', nlLocale);

@NgModule({
  declarations: [
    AppComponent
  ],
  bootstrap: [ AppComponent ], imports: [ BrowserModule,
    NgxsModule.forRoot([
      ApplicationState,
      AuthState,
      NavigationState,
      FileUploadState,
      ScreeningQuestionnaireState,
      RegistrationState,
      MoodState,
      PatientDailyNotesState
    ], {
      developmentMode: !ApplicationConfig.production
    }),
    NgxsLoggerPluginModule.forRoot({
      disabled: ApplicationConfig.production
    }),
    NgxsStoragePluginModule.forRoot(NgxsStorageConfig),
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: ApplicationConfig.production
    }),
    NgxsRouterPluginModule.forRoot(),
    AppRoutingModule,
    LayoutModule,
    TranslateModule.forRoot(TranslateConfig),
    AngularSvgIconModule.forRoot(),
    ModalModule.forRoot(),
    ProgressbarModule.forRoot(),
    ToastrModule.forRoot(ToastrConfig),
    NgSelectModule,
    BsDatepickerModule.forRoot(),
    ReactiveFormsModule,
    NavigationModule,
    MeetingModule,
    NgxSpinnerModule.forRoot(NgxSpinnerConfig),
    FormsModule,
    SharedModule ], providers: [
    provideAppInitializer(() => {
      const initializerFn = ((_applicationModel: ApplicationModel) => () => {
      })(inject(ApplicationModel));
      return initializerFn();
    }),
    {
      provide: LOCALE_ID,
      useClass: LocaleId,
      deps: [ Store ]
    },
    { provide: AUTH_PROVIDER, useClass: CognitoAuthProvider },
    {
      provide: NgSelectConfig,
      useValue: { disableVirtualScroll: true, openOnEnter: false, outsideClickEvent: 'mousedown' }
    },
    { provide: BsDatepickerConfig, useFactory: getDatepickerConfig },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: AppLanguageInterceptor },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: LoaderInterceptor },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: AppVersionInterceptor },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: AppTimezoneInterceptor },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: ErrorInterceptor },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: AccessTokenInterceptor },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: RefreshTokenInterceptor },
    { provide: MESSAGE_FORMAT_CONFIG, useValue: MessageFormatConfig },
    DatePipe,
    TranslatePipe,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations()
  ]
})
export class AppModule {
  constructor() {
  }
}
